import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';

/*
 * RegenerateButton — self-serve "Regenerate" for the live brief, available from EVERY briefing
 * state (ready, quiet-day, and pending/not_generated), not just when a ready brief already exists.
 * The regenerate route runs generateBriefing regardless of whether a row exists, so a user who
 * lands on a quiet/pending day (e.g. a meetings-only day before the 3 AM sweep — bug-418) can force
 * their briefing now instead of waiting. It is deliberately NOT shown on the no_team state (that
 * route 404s) or in readOnly history detail (that would regenerate TODAY's brief while viewing a
 * past one) — callers gate those.
 *
 * apiFetch attaches the Bearer token + api-base URL (no console token-scraping), throws on non-2xx,
 * then we invalidate the team-today key so the surface refetches the freshly generated brief. Slow
 * (LLM generation): the button disables + spins in flight; a failure surfaces as inline error text
 * rather than a silent no-op. Self-contained (owns its own in-flight/error state) so it drops into
 * any surface without threading state through it.
 */
export function RegenerateButton({
  className = 'btn btn-secondary',
  label = 'Regenerate',
  busyLabel = 'Regenerating…',
}: {
  className?: string;
  // D3 (2026-08-12): the freshness banner reuses this button as its "Build today's" action —
  // same POST, same invalidation, different words. Defaults keep every existing call site as-is.
  label?: string;
  busyLabel?: string;
}) {
  const queryClient = useQueryClient();
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const regenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    setError(null);
    try {
      await apiFetch('/api/surfaces/team/today/regenerate', { method: 'POST' });
      await queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-today'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Regeneration failed. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
      <button
        type="button"
        className={className}
        onClick={regenerate}
        disabled={regenerating}
        aria-busy={regenerating}
      >
        {regenerating ? (
          <>
            <span className="brief-regen-spin" aria-hidden /> {busyLabel}
          </>
        ) : (
          <>
            {label} <span className="arr">↻</span>
          </>
        )}
      </button>
      {error && (
        <span className="brief-regen-error" role="alert" style={{ margin: 0 }}>
          {error}
        </span>
      )}
    </span>
  );
}
